import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Container, Row } from 'react-bootstrap';
import {
  FaBuilding,
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaHourglassHalf,
  FaSitemap,
  FaTachometerAlt,
  FaUserCog,
  FaUserTie,
  FaUsers
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import BusinessAuditProgress from '../../Components/BusinessAuditProgress';


function BusinessDashboard() {
  const [stats, setStats] = useState({
    branchesCount: 0,
    departmentsCount: 0,
    departmentUsersCount: 0,
    auditorsCount: 0,
    checklistsCount: 0,
  });
  const [checklistStatus, setChecklistStatus] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        // Get admin data from local storage
        const userData = JSON.parse(localStorage.getItem('user'));
        setAdmin(userData);
        
        if (!userData || !userData.CompanyCode) {
          throw new Error('Admin data not found');
        }

        // Try to fetch profile image if available
        if (userData.id) {
          try {
            const profileResponse = await fetch(`${BASE_URL}/get-business-user-data/${userData.id}`);
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              if (profileData.profileImage) {
                setProfileImage(`${BASE_URL}${profileData.profileImage}`);
              }
            }
          } catch (profileError) {
            console.error("Failed to load profile image:", profileError);
            // Continue with stats loading even if profile image fails
          }
        }
        
        // Use the new combined endpoint to get all dashboard stats in one request
        const response = await fetch(`${BASE_URL}/get-business-admin-dashboard-stats/${userData.CompanyCode}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch dashboard statistics');
        }
        
        const data = await response.json();
        
        if (data.success) {
          // Set the stats from the combined API response
          setStats(data.stats);
          setChecklistStatus(data.checklistStatus);
        } else {
          throw new Error(data.error || 'Failed to fetch dashboard statistics');
        }
        
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [BASE_URL]);

  const getAdminInitials = () => {
    if (!admin) return "AD";
    return `${admin.company?.charAt(0) || ''} `.toUpperCase();
  };

  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  return (
    <Container fluid className="p-0">
      {/* Dashboard Header */}
      <div className="dashboard-header bg-primary text-white">
        <Container fluid className="px-4 py-4">
          <Row className="align-items-center">
            <Col lg={6}>
              <div className="d-flex align-items-center">
                {profileImage ? (
                  <div className="admin-avatar me-3">
                    <img 
                      src={profileImage} 
                      alt={admin.company}
                      className="profile-image"
                    />
                  </div>
                ) : (
                  <div className="admin-avatar me-3">
                    {getAdminInitials()}
                  </div>
                )}
                <div>
                  <h4 className="greeting mb-0">Welcome back, {admin?.company || 'Admin'}!</h4>
                  <p className="mb-0 opacity-75">
                    <FaCalendarAlt className="me-1" size={14} /> {currentDate}
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="text-lg-end mt-3 mt-lg-0">
              <Badge bg="white" text="primary" className="stats-badge">
                <FaTachometerAlt className="me-1" /> Admin Dashboard
              </Badge>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Dashboard Content */}
      <Container fluid className="px-4 py-4 dashboard-content">
        {error && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Error Loading Dashboard</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Dashboard</p>
            <p className="text-muted">Please wait while we fetch your stats...</p>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <Row className="g-4 mb-4">
              <Col xs={12}>
                <h5 className="section-title mb-3">
                  <FaChartLine className="me-2" /> Organization Statistics
                </h5>
              </Col>
              
              {/* Branches Card */}
              <Col xs={12} sm={6} md={4} lg={15} xl={15} className="col-lg-1-5">
                <Link to="/BusinessBranch" className="text-decoration-none">
                  <Card className="stat-card border-0 shadow-sm h-100 clickable-card">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <div className="stat-icon branch-icon">
                          <FaBuilding />
                        </div>
                        <h5 className="stat-label ms-3 mb-0">Branches</h5>
                      </div>
                      <div className="text-center">
                        <h3 className="stat-value">{stats.branchesCount}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              {/* Departments Card */}
              <Col xs={12} sm={6} md={4} lg={15} xl={15} className="col-lg-1-5">
                <Link to="/BusinessDepartment" className="text-decoration-none">
                  <Card className="stat-card border-0 shadow-sm h-100 clickable-card">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <div className="stat-icon department-icon">
                          <FaSitemap />
                        </div>
                        <h5 className="stat-label ms-3 mb-0">Departments</h5>
                      </div>
                      <div className="text-center">
                        <h3 className="stat-value">{stats.departmentsCount}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              {/* Department Users Card */}
              <Col xs={12} sm={6} md={4} lg={15} xl={15} className="col-lg-1-5">
                <Link to="/BusinessDepartmentUser" className="text-decoration-none">
                  <Card className="stat-card border-0 shadow-sm h-100 clickable-card">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <div className="stat-icon users-icon">
                          <FaUsers />
                        </div>
                        <h5 className="stat-label ms-3 mb-0">Department Users</h5>
                      </div>
                      <div className="text-center">
                        <h3 className="stat-value">{stats.departmentUsersCount}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              {/* Auditors Card */}
              <Col xs={12} sm={6} md={4} lg={15} xl={15} className="col-lg-1-5">
                <Link to="/BusinessAuditorCreate" className="text-decoration-none">
                  <Card className="stat-card border-0 shadow-sm h-100 clickable-card">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <div className="stat-icon auditor-icon">
                          <FaUserTie />
                        </div>
                        <h5 className="stat-label ms-3 mb-0">Auditors</h5>
                      </div>
                      <div className="text-center">
                        <h3 className="stat-value">{stats.auditorsCount}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              {/* Checklists Card */}
              <Col xs={12} sm={6} md={4} lg={15} xl={15} className="col-lg-1-5">
                <Link to="/BusinessChecklistOverview" className="text-decoration-none">
                  <Card className="stat-card border-0 shadow-sm h-100 clickable-card">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        <div className="stat-icon checklist-icon">
                          <FaClipboardList />
                        </div>
                        <h5 className="stat-label ms-3 mb-0">Checklists</h5>
                      </div>
                      <div className="text-center">
                        <h3 className="stat-value">{stats.checklistsCount}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
            </Row>

            {/* Checklist Status Section */}
            <Row className="g-4 mb-4">
              <Col xs={12}>
                <h5 className="section-title mb-3">
                  <FaClipboardList className="me-2" /> Audit Status
                </h5>
              </Col>
              
              {/* All Checklists */}
              <Col xs={12} sm={6} md={3}>
                <Link to="/BusinessAuditOverview" className="text-decoration-none">
                  <Card className="status-card border-0 shadow-sm clickable-card">
                    <Card.Body className="d-flex align-items-center">
                      <div className="status-icon all-icon">
                        <FaClipboardList />
                      </div>
                      <div className="ms-3">
                        <div className="status-label">All</div>
                        <h3 className="status-value mb-0">{checklistStatus.pending + checklistStatus.inProgress + checklistStatus.completed}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              {/* Pending Checklists */}
              <Col xs={12} sm={6} md={3}>
                <Link to="/BusinessAuditOverview?status=pending" className="text-decoration-none">
                  <Card className="status-card border-0 shadow-sm clickable-card">
                    <Card.Body className="d-flex align-items-center">
                      <div className="status-icon pending-icon">
                        <FaClock />
                      </div>
                      <div className="ms-3">
                        <div className="status-label">Pending</div>
                        <h3 className="status-value mb-0">{checklistStatus.pending}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              {/* In Progress Checklists */}
              <Col xs={12} sm={6} md={3}>
                <Link to="/BusinessAuditOverview?status=inProgress" className="text-decoration-none">
                  <Card className="status-card border-0 shadow-sm clickable-card">
                    <Card.Body className="d-flex align-items-center">
                      <div className="status-icon progress-icon">
                        <FaHourglassHalf />
                      </div>
                      <div className="ms-3">
                        <div className="status-label">In Progress</div>
                        <h3 className="status-value mb-0">{checklistStatus.inProgress}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              {/* Completed Checklists */}
              <Col xs={12} sm={6} md={3}>
                <Link to="/BusinessAuditOverview?status=completed" className="text-decoration-none">
                  <Card className="status-card border-0 shadow-sm clickable-card">
                    <Card.Body className="d-flex align-items-center">
                      <div className="status-icon complete-icon">
                        <FaCheckCircle />
                      </div>
                      <div className="ms-3">
                        <div className="status-label">Completed</div>
                        <h3 className="status-value mb-0">{checklistStatus.completed}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
            </Row>

            <BusinessAuditProgress/>
            
            {/* Quick Actions Section */}
            <Row className="g-4">
              <Col xs={12}>
                <h5 className="section-title mb-3">
                  <FaUserCog className="me-2" /> Quick Actions
                </h5>
              </Col>
              <Col xs={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <Row className="g-3">
                      <Col xs={6} sm={4} md={2}>
                        <Link to="/BusinessBranch" className="action-card">
                          <div className="action-icon bg-primary-subtle text-primary">
                            <FaBuilding />
                          </div>
                          <span>Add Branch</span>
                        </Link>
                      </Col>
                      <Col xs={6} sm={4} md={2}>
                        <Link to="/BusinessDepartment" className="action-card">
                          <div className="action-icon bg-warning-subtle text-warning">
                            <FaSitemap />
                          </div>
                          <span>Add Department</span>
                        </Link>
                      </Col>
                      <Col xs={6} sm={4} md={2}>
                        <Link to="/BusinessDepartmentUser" className="action-card">
                          <div className="action-icon bg-primary-subtle text-primary">
                            <FaUsers />
                          </div>
                          <span>Add Department User</span>
                        </Link>
                      </Col>
                      <Col xs={6} sm={4} md={2}>
                        <Link to="/BusinessAuditorCreate" className="action-card">
                          <div className="action-icon bg-success-subtle text-success">
                            <FaUserTie />
                          </div>
                          <span>Add Auditor</span>
                        </Link>
                      </Col>
                      
                      <Col xs={6} sm={4} md={2}>
                        <Link to="/BusinessChecklistOverview" className="action-card">
                          <div className="action-icon bg-info-subtle text-info">
                            <FaClipboardList />
                          </div>
                          <span>New Checklist</span>
                        </Link>
                      </Col>
                     
                      <Col xs={6} sm={4} md={2}>
                        <Link to={`/BusinessUserProfile/${admin?.id}`} className="action-card">
                          <div className="action-icon bg-secondary-subtle text-secondary">
                            <FaUserCog />
                          </div>
                          <span>Settings</span>
                        </Link>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>

      <style>
        {`
          /* Dashboard Header Styles */
          .dashboard-header {
            background-image: linear-gradient(135deg, var(--bs-primary) 0%, #4361ee 100%);
            border-bottom-left-radius: 20px;
            border-bottom-right-radius: 20px;
            box-shadow: 0 4px 12px rgba(var(--bs-primary-rgb), 0.1);
          }
          
          .greeting {
            font-weight: 600;
          }
          
          .admin-avatar {
            width: 48px;
            height: 48px;
            background-color: rgba(255, 255, 255, 0.2);
            border: 2px solid rgba(255, 255, 255, 0.5);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 18px;
            color: white;
            overflow: hidden;
          }
          
          .profile-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .stats-badge {
            padding: 8px 16px;
            font-weight: 500;
            border-radius: 30px;
          }
          
          /* Dashboard Content Styles */
          .dashboard-content {
            background-color: #f8f9fa;
            min-height: calc(100vh - 82px);
          }
          
          .section-title {
            font-weight: 600;
            color: #344767;
            display: flex;
            align-items: center;
          }
          
          /* Stat Cards */
          .stat-card, .status-card {
            border-radius: 12px;
            overflow: hidden;
            transition: all 0.3s ease;
          }
          
          .stat-card .card-body {
            padding: 1.5rem;
            height: 100%;
          }
          
          .status-card .card-body {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            text-align: left;
            padding: 1.5rem;
            height: 100%;
          }
          
          .stat-card:hover, .status-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
          }
          
          .stat-icon, .status-icon {
            width: 45px;
            height: 45px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
          }
          
          .branch-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .department-icon {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .users-icon {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .auditor-icon {
            background-color: rgba(13, 202, 240, 0.1);
            color: #0dcaf0;
          }
          
          .checklist-icon {
            background-color: rgba(220, 53, 69, 0.1);
            color: #dc3545;
          }
          
          .config-icon {
            background-color: rgba(108, 117, 125, 0.1);
            color: #6c757d;
          }
          
          .pending-icon {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .progress-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .complete-icon {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .all-icon {
            background-color: rgba(108, 117, 125, 0.1);
            color: #6c757d;
          }
          
          .stat-value, .status-value {
            font-weight: 700;
            font-size: 32px;
            margin-bottom: 8px;
          }
          
          .stat-label, .status-label {
            font-size: 14px;
            font-weight: 500;
            color: #6c757d;
            margin-bottom: 0;
          }
          
          .stat-link {
            color: var(--bs-primary);
            font-weight: 500;
            font-size: 14px;
            text-decoration: none;
            display: inline-block;
          }
          
          /* Status card specific layout adjustments */
          .status-card .status-icon {
            margin-bottom: 0;
            margin-right: 15px;
          }
          
          /* Action Cards */
          .action-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 15px;
            border-radius: 12px;
            background-color: white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            transition: all 0.3s ease;
            text-decoration: none;
            color: #344767;
            height: 100%;
          }
          
          .action-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 15px rgba(0,0,0,0.1);
          }
          
          .action-icon {
            width: 50px;
            height: 50px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 22px;
            margin-bottom: 10px;
          }
          
          .action-card span {
            font-weight: 500;
            font-size: 14px;
          }
          
          /* Responsive Adjustments */
          @media (max-width: 992px) {
            .stat-card .stat-label, .status-card .status-label {
              font-size: 12px;
            }
            
            .stat-value, .status-value {
              font-size: 28px;
            }
          }
          
          @media (max-width: 768px) {
            .action-icon {
              width: 40px;
              height: 40px;
              font-size: 18px;
            }
            
            .action-card span {
              font-size: 12px;
            }
          }
          
          @media (max-width: 576px) {
            .dashboard-header {
              border-radius: 0;
            }
            
            .greeting {
              font-size: 1.25rem;
            }
            
            .stat-card, .status-card {
              height: auto;
            }
            
            .stat-value, .status-value {
              font-size: 24px;
            }
          }

          /* Custom 5-column layout */
          @media (min-width: 992px) {
            .col-lg-1-5 {
              flex: 0 0 20%;
              max-width: 20%;
            }
          }

          @media (min-width: 1200px) {
            .col-xl-1-5 {
              flex: 0 0 20%;
              max-width: 20%;
            }
          }

          /* Add styling for clickable cards */
          .clickable-card {
            cursor: pointer;
            transition: all 0.3s ease;
          }
          
          .clickable-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.15) !important;
          }
        `}
      </style>
    </Container>
  );
}

export default BusinessDashboard;